import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '@/store/languageStore';
import type { SourceOrganization } from '@/services/migration/migrationService';
import {
  buildSourceOrgTree,
  collectExpandableIds,
  countDescendants,
  filterSourceOrgTree,
  flattenSourceOrgTree,
  type SourceOrgTreeNode,
} from './sourceOrgTree';

interface MigrationOrgPickerProps {
  organizations: SourceOrganization[];
  selectedIds: Set<number>;
  onToggle: (orgId: number) => void;
  onReplaceSelection: (ids: Set<number>) => void;
}

export const MigrationOrgPicker = ({
  organizations,
  selectedIds,
  onToggle,
  onReplaceSelection,
}: MigrationOrgPickerProps) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  const tree = useMemo(() => buildSourceOrgTree(organizations), [organizations]);
  const filteredTree = useMemo(
    () => filterSourceOrgTree(tree, searchQuery),
    [tree, searchQuery]
  );
  const topRoots = filteredTree.filter(node => node.org.o_topnode === 1);
  const otherRoots = filteredTree.filter(node => node.org.o_topnode !== 1);

  const previousSearch = useRef('');
  useEffect(() => {
    const hadSearch = previousSearch.current.trim().length > 0;
    const hasSearch = searchQuery.trim().length > 0;
    previousSearch.current = searchQuery;
    if (hasSearch) {
      setExpandedIds(new Set(collectExpandableIds(filteredTree)));
      return;
    }
    if (hadSearch) setExpandedIds(new Set());
  }, [searchQuery, filteredTree]);

  const isOpen = (orgId: number) => expandedIds.has(orgId);

  const toggleExpanded = (orgId: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(orgId)) next.delete(orgId);
      else next.add(orgId);
      return next;
    });
  };

  const selectVisible = () => {
    onReplaceSelection(new Set(flattenSourceOrgTree(filteredTree).map(org => org.o_id)));
  };

  const expandAll = () => {
    setExpandedIds(new Set(collectExpandableIds(filteredTree)));
  };

  const collapseAll = () => {
    setExpandedIds(new Set());
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[14rem] max-w-sm">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-textTertiary" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder={t('migration.searchOrganizations')}
            className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-lg bg-bgElevated text-textDefault placeholder:text-textTertiary focus:outline-none focus:ring-1 focus:ring-bgPrimary"
          />
        </div>
        <button
          type="button"
          onClick={selectVisible}
          className="px-3 py-2 text-xs rounded-lg border border-border bg-bgElevated text-textDefault hover:bg-bgLight"
        >
          {t('migration.selectAll')}
        </button>
        <button
          type="button"
          onClick={() => onReplaceSelection(new Set())}
          className="px-3 py-2 text-xs rounded-lg border border-border bg-bgElevated text-textDefault hover:bg-bgLight"
        >
          {t('migration.deselectAll')}
        </button>
        <button
          type="button"
          onClick={expandAll}
          className="px-3 py-2 text-xs rounded-lg border border-border bg-bgElevated text-textDefault hover:bg-bgLight"
        >
          {t('migration.expandAll')}
        </button>
        <button
          type="button"
          onClick={collapseAll}
          className="px-3 py-2 text-xs rounded-lg border border-border bg-bgElevated text-textDefault hover:bg-bgLight"
        >
          {t('migration.collapseAll')}
        </button>
        <span className="text-sm text-textSecondary ml-auto">
          {t('migration.selectedCount', { count: selectedIds.size })}
        </span>
      </div>

      <div className="border border-border rounded-lg overflow-hidden mb-6 max-h-[28rem] overflow-y-auto">
        {filteredTree.length === 0 ? (
          <div className="py-10 text-center text-sm text-textTertiary">
            {t('migration.noSearchResults')}
          </div>
        ) : (
          <>
            {topRoots.map(node => (
              <OrgBranch
                key={node.org.o_id}
                node={node}
                depth={0}
                selectedIds={selectedIds}
                isOpen={isOpen}
                onToggleExpanded={toggleExpanded}
                onToggleSelected={onToggle}
                topLabel={t('migration.topNode')}
                childrenLabel={t('migration.children')}
              />
            ))}
            {otherRoots.length > 0 && topRoots.length > 0 && (
              <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-textTertiary bg-bgLight border-y border-border">
                {t('migration.otherOrganizations')}
              </div>
            )}
            {otherRoots.map(node => (
              <OrgBranch
                key={node.org.o_id}
                node={node}
                depth={0}
                selectedIds={selectedIds}
                isOpen={isOpen}
                onToggleExpanded={toggleExpanded}
                onToggleSelected={onToggle}
                topLabel={t('migration.topNode')}
                childrenLabel={t('migration.children')}
              />
            ))}
          </>
        )}
      </div>
    </>
  );
};

interface OrgBranchProps {
  node: SourceOrgTreeNode;
  depth: number;
  selectedIds: Set<number>;
  isOpen: (orgId: number) => boolean;
  onToggleExpanded: (orgId: number) => void;
  onToggleSelected: (orgId: number) => void;
  topLabel: string;
  childrenLabel: string;
}

const OrgBranch = ({
  node,
  depth,
  selectedIds,
  isOpen,
  onToggleExpanded,
  onToggleSelected,
  topLabel,
  childrenLabel,
}: OrgBranchProps) => {
  const { org, children } = node;
  const hasChildren = children.length > 0;
  const open = hasChildren && isOpen(org.o_id);
  const isSelected = selectedIds.has(org.o_id);
  const descendantCount = countDescendants(node);
  const isTop = org.o_topnode === 1;

  return (
    <div className={depth === 0 ? 'border-b border-border last:border-b-0' : undefined}>
      <div
        className={`flex items-center gap-2 pr-4 py-2.5 cursor-pointer transition-colors ${
          depth > 0 ? 'border-t border-border' : ''
        } ${
          isSelected
            ? 'bg-blue-50 dark:bg-blue-900/20'
            : depth === 0
              ? 'bg-bgContainer hover:bg-bgLight'
              : 'hover:bg-bgElevated'
        }`}
        style={{ paddingLeft: `${12 + depth * 22}px` }}
        onClick={() => onToggleSelected(org.o_id)}
      >
        {hasChildren ? (
          <button
            type="button"
            aria-expanded={open}
            aria-label={org.o_name}
            className="w-5 h-5 flex items-center justify-center rounded text-textSecondary hover:bg-bgLight flex-shrink-0"
            onClick={event => {
              event.stopPropagation();
              onToggleExpanded(org.o_id);
            }}
          >
            {open ? (
              <ChevronDownIcon className="w-4 h-4" />
            ) : (
              <ChevronRightIcon className="w-4 h-4" />
            )}
          </button>
        ) : (
          <span className="w-5 flex-shrink-0" />
        )}
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggleSelected(org.o_id)}
          className="w-4 h-4 border border-border rounded bg-bgElevated cursor-pointer flex-shrink-0"
          onClick={event => event.stopPropagation()}
        />
        <div className="flex-1 min-w-0">
          <span
            className={`text-sm truncate block ${
              depth === 0 || isTop ? 'font-semibold text-textDefault' : 'font-medium text-textDefault'
            }`}
            title={org.o_name}
          >
            {org.o_name}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isTop && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-medium">
              {topLabel}
            </span>
          )}
          {descendantCount > 0 && (
            <span className="text-xs text-textTertiary">
              {descendantCount} {childrenLabel}
            </span>
          )}
          <span className="text-xs text-textTertiary font-mono">#{org.o_id}</span>
        </div>
      </div>
      {open && (
        <div className={depth === 0 ? 'border-l-2 border-bgPrimary/30 ml-5' : undefined}>
          {children.map(child => (
            <OrgBranch
              key={child.org.o_id}
              node={child}
              depth={depth + 1}
              selectedIds={selectedIds}
              isOpen={isOpen}
              onToggleExpanded={onToggleExpanded}
              onToggleSelected={onToggleSelected}
              topLabel={topLabel}
              childrenLabel={childrenLabel}
            />
          ))}
        </div>
      )}
    </div>
  );
};
